import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { HubStatusBadge, MarketplaceTag } from "../components/StatusBadge";
import { toast } from "sonner";
import { Loader2, PackageOpen, Box, Truck, RefreshCw } from "lucide-react";

const STAGES = [
  { id: "ready", label: "Aguardando separação", filter: ["nf_sent", "label_ready", "in_separation"] },
  { id: "separated", label: "Separados", filter: ["separated"] },
  { id: "packed", label: "Embalados", filter: ["packed"] },
  { id: "dispatched", label: "Despachados", filter: ["dispatched"] },
];

const ACTIONS = {
  ready: { next: "separated", label: "Marcar separado", icon: PackageOpen, color: "#06B6D4" },
  separated: { next: "packed", label: "Marcar embalado", icon: Box, color: "#A855F7" },
  packed: { next: "dispatched", label: "Marcar despachado", icon: Truck, color: "#0C4A6E" },
};

export default function Expedition() {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setAllOrders(data);
    } catch (e) {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (order, action) => {
    setBusy(order.id);
    try {
      await api.post(`/orders/${order.id}/expedition`, { action });
      toast.success(`Pedido ${order.order_number} → ${action}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="expedition-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Operação</div>
          <h1 className="text-3xl font-black tracking-tighter">Conferência de Expedição</h1>
          <p className="text-sm text-slate-600 mt-1">Acompanhe e movimente pedidos entre estágios da expedição.</p>
        </div>
        <button onClick={load} data-testid="expedition-refresh" className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="inline w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {STAGES.map((stage) => {
            const items = allOrders.filter((o) => stage.filter.includes(o.hub_status));
            const action = ACTIONS[stage.id];
            const Icon = action?.icon;
            return (
              <div key={stage.id} className="border border-slate-200 bg-white flex flex-col" data-testid={`stage-${stage.id}`}>
                <div className="px-3 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{stage.label}</div>
                    <div className="text-2xl font-black tracking-tighter">{items.length}</div>
                  </div>
                  {Icon && <Icon className="w-5 h-5 text-slate-400" />}
                </div>
                <div className="flex-1 overflow-y-auto max-h-[60vh] divide-y divide-slate-100">
                  {items.length === 0 && (<div className="px-3 py-6 text-center text-xs text-slate-400">Vazio</div>)}
                  {items.map((o) => (
                    <div key={o.id} className="px-3 py-3" data-testid={`stage-card-${o.order_number}`}>
                      <div className="flex items-center justify-between mb-1">
                        <MarketplaceTag id={o.marketplace} />
                        <div className="font-mono text-xs font-bold">{o.order_number}</div>
                      </div>
                      <div className="text-xs text-slate-700 mb-1">{o.customer_name}</div>
                      <div className="text-[10px] font-mono text-slate-500 mb-2">
                        {o.items.map((it) => `${it.sku}×${it.quantity}`).join("  ·  ")}
                      </div>
                      <div className="mb-2"><HubStatusBadge status={o.hub_status} /></div>
                      {action && (
                        <button
                          data-testid={`stage-action-${stage.id}-${o.order_number}`}
                          onClick={() => act(o, action.next)}
                          disabled={busy === o.id}
                          className="w-full px-2 py-1.5 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-1.5 disabled:opacity-60 transition-opacity"
                          style={{ background: action.color }}
                        >
                          {busy === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <action.icon className="w-3.5 h-3.5" />}
                          {action.label}
                        </button>
                      )}
                      {!action && (
                        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold text-center pt-1">
                          ✓ Concluído
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
